import { useEffect, useRef } from 'react';

type Props = {
  /** Bornes du ratio gauche (entre 0 et 1). */
  min?: number;
  max?: number;
  /** Ratio actuel (gauche / total) — contrôlé par le parent. */
  ratio: number;
  /** Callback appelé pendant et après le drag. Le parent persiste lui-même. */
  onRatioChange: (next: number) => void;
};

/**
 * Poignée verticale draggable entre deux colonnes d'une grid CSS.
 *
 * Le parent contrôle le ratio (state + grid-template-columns) et persiste
 * lui-même dans localStorage à la fin du drag.
 *
 * Mécanique :
 *  - mousedown sur la poignée → on démarre le drag (ref droite-x du conteneur).
 *  - mousemove window → calcul ratio relatif au parent.
 *  - mouseup window → fin de drag, le parent peut écrire localStorage.
 */
export default function Splitter({ ratio, onRatioChange, min = 0.3, max = 0.85 }: Props) {
  const draggingRef = useRef(false);
  const containerRectRef = useRef<DOMRect | null>(null);
  const handleRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!draggingRef.current || !containerRectRef.current) return;
      const rect = containerRectRef.current;
      // ratio = position relative dans le parent, bornée par min/max.
      const next = Math.min(max, Math.max(min, (e.clientX - rect.left) / rect.width));
      onRatioChange(next);
      e.preventDefault();
    };
    const onUp = () => {
      if (!draggingRef.current) return;
      draggingRef.current = false;
      containerRectRef.current = null;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [min, max, onRatioChange]);

  const onDown = (e: React.MouseEvent) => {
    // Le rect du parent grid donne la zone de référence pour le calcul ratio.
    const grid = handleRef.current?.parentElement;
    if (!grid) return;
    containerRectRef.current = grid.getBoundingClientRect();
    draggingRef.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    e.preventDefault();
  };

  return (
    <div
      ref={handleRef}
      role="separator"
      aria-orientation="vertical"
      aria-valuenow={Math.round(ratio * 100)}
      onMouseDown={onDown}
      className="group relative cursor-col-resize bg-rule transition-colors hover:bg-accent/50"
      // 4px visible, hit area élargie via ::before invisible
      style={{ width: 4 }}
    >
      <div className="absolute inset-y-0 -inset-x-2" />
    </div>
  );
}
